import apiClient from '@/scripts/api/apiClient';

/**
 * Video API endpoints
 */

// Query key factories
export const videoKeys = {
  getAllVideos: () => ['get', 'videos'],
  getVideo: (videoId) => ['get', 'videos', videoId],
  updateVideo: (videoId) => ['update', 'videos', videoId],
  deleteVideo: (videoId) => ['delete', 'videos', videoId],
  getVideoShareLinks: (videoId) => ['get', 'videos', videoId, 'shareLinks'],
  createVideoShareLink: (videoId) => ['create', 'videos', videoId, 'shareLinks'],
  deleteVideoShareLink: (shareLinkId) => ['delete', 'videos', 'shareLinks', shareLinkId],
  getPublicVideo: (shortId) => ['get', 'share', 'video', shortId],
};

/**
 * Get all videos
 * @returns {Promise<Array>} List of videos
 */
export const getAllVideos = async () => {
  const response = await apiClient.get('/api/videos');
  return response.data;
};

/**
 * Get a specific video by ID
 * @param {string} id - Video ID
 * @returns {Promise<Object>} Video details
 */
export const getVideo = async (id) => {
  if (!id) throw new Error('Video ID is required');
  const response = await apiClient.get(`/api/videos/${id}`);
  return response.data;
};

/**
 * Update a video
 * @param {string} id - Video ID
 * @param {Object} data - Video data to update
 * @returns {Promise<Object>} Updated video
 */
export const updateVideo = async (id, data) => {
  if (!id) throw new Error('Video ID is required');
  const response = await apiClient.put(`/api/videos/${id}`, data);
  return response.data;
};

/**
 * Delete a video
 * @param {string} id - Video ID
 * @returns {Promise<Object>} Response data
 */
export const deleteVideo = async (id) => {
  if (!id) throw new Error('Video ID is required');
  const response = await apiClient.delete(`/api/videos/${id}`);
  return response.data;
};

/**
 * Get share links for a video
 * @param {string} videoId - Video ID
 * @returns {Promise<Array>} List of share links for the video
 */
export const getVideoShareLinks = async (videoId) => {
  if (!videoId) return [];
  const response = await apiClient.get(`/api/videos/${videoId}/share-links`);
  return response.data;
};

/**
 * Create a share link for a video
 * @param {string} videoId - Video ID
 * @param {Object} shareData - Share link data
 * @returns {Promise<Object>} Created share link
 */
export const createShareLink = async (videoId, shareData) => {
  if (!videoId) throw new Error('Video ID is required');
  const response = await apiClient.post(`/api/videos/${videoId}/share-links`, shareData);
  return response.data;
};

/**
 * Delete a share link
 * @param {string} shareLinkId - Share link ID
 * @returns {Promise<Object>} Response data
 */
export const deleteShareLink = async (shareLinkId) => {
  if (!shareLinkId) throw new Error('Share link ID is required');
  const response = await apiClient.delete(`/api/share-link/video/${shareLinkId}`);
  return response.data;
};

/**
 * Get a public video by short ID
 * @param {string} shortId - Short ID for the shared video
 * @returns {Promise<Object>} Public video data
 */
export const getPublicVideo = async (shortId) => {
  if (!shortId) throw new Error('Short ID is required');
  const response = await apiClient.get(`/api/share/video/${shortId}`);
  return response.data;
};
